/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpStatusCode
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CRouteList } from '@app/route-list.const';
import { StorageService } from '@core/storage/storage.service';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, tap, take } from 'rxjs/operators';
import { EAuthEndpoints } from '../auth/auth.model';
import { AuthService } from '../auth/auth.service';
import { RollerRecaptchaService } from '../roller-recaptcha/roller-recaptcha.service';
import { CStorageKeys } from '../storage/storage-keys.const';
import { VenueConfigService } from '../venue-config/venue-config.service';

@Injectable()
export class HttpApiInterceptor implements HttpInterceptor {
  readonly storageKeys = CStorageKeys;
  readonly routes = CRouteList;
  constructor(
    private storageService: StorageService,
    private router: Router,
    private venueConfigService: VenueConfigService,
    private rollerRecaptchaService: RollerRecaptchaService,
    private authService: AuthService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const apiUrl: string | null = this.storageService.getItem<string>(this.storageKeys.api, false);
    if (!apiUrl || !request.url.startsWith(apiUrl)) {
      return next.handle(request);
    }

    let headers = request.headers;

    headers = this.setHeaderFromStorageKey(headers, this.storageKeys.apiKey);
    headers = this.setHeaderFromStorageKey(headers, this.storageKeys.cellId);
    headers = this.setHeaderFromStorageKey(headers, this.storageKeys.originId);

    let isGuestLoggedIn = false;

    return this.authService.getIsLoggedIn().pipe(
      take(1),
      switchMap((isLoggedIn: boolean) => {
        isGuestLoggedIn = isLoggedIn || this.authService.isAuthenticated();

        if (isGuestLoggedIn || request.url.includes(EAuthEndpoints.logout)) {
          headers = this.setAuthHeader(headers);
        }

        request = request.clone({ headers });
        return next.handle(request);
      }),
      filter((event: any) => event instanceof HttpResponse),
      tap((event: HttpResponse<any>) => {
        const cellId = event.headers.get(this.storageKeys.cellId);

        if (cellId) {
          this.storageService.setItem(this.storageKeys.cellId, cellId, false);
        }
      }),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          const err = error as HttpErrorResponse;
          if (err.status === HttpStatusCode.NotFound) {
            this.router.navigate([this.routes.maintenance]);
          } else if (err.status === HttpStatusCode.BadRequest) {
            this.router.navigate([this.routes.error]);
          } else if (this.isRecaptchaRequiredRequest(err)) {
            this.rollerRecaptchaService.setRecaptchaEnabledAutomatically();
          } else if (this.isAuthRequestUnauthorized(isGuestLoggedIn, err, request)) {
            return this.authService.signOut().pipe(switchMap(() => throwError(() => err)));
          }

          return throwError(() => err);
        }

        return throwError(() => new Error('Unknown error'));
      })
    );
  }

  private isRecaptchaRequiredRequest(err: HttpErrorResponse): boolean {
    return (
      err.status === HttpStatusCode.Conflict &&
      !!err.error.message &&
      err.error.message.toLocaleLowerCase().includes('jwt')
    );
  }

  /** Returns true when the guest is logged in, and a request is made that throws 401 and is NOT the logout endpoint */
  private isAuthRequestUnauthorized(
    isGuestLoggedIn: boolean,
    err: HttpErrorResponse,
    request: HttpRequest<unknown>
  ): boolean {
    return (
      isGuestLoggedIn && err.status === HttpStatusCode.Unauthorized && !request.url.includes(EAuthEndpoints.logout)
    );
  }

  /** Takes current headers and appends the additional key from session storage. */
  private setHeaderFromStorageKey(headers: HttpHeaders, key: string): HttpHeaders {
    const item = this.storageService.getItem<string>(key, false);
    if (item) {
      headers = headers.append(key, item);
    }

    return headers;
  }

  /** Fetches the local storage auth JWT and appends it as a authorization bearer token. */
  private setAuthHeader(headers: HttpHeaders): HttpHeaders {
    const authCredentials = this.authService.getAuthCredentials();
    if (authCredentials?.token) {
      headers = headers.append('Authorization', 'Bearer ' + authCredentials.token);
    }

    return headers;
  }
}
